import { BBOT_DJANGO_SERVER } from "Config";
import { BbotAlert, OPContainer, PageLoadingSpinner, Space } from "top-component-library";
import { getDoveTailToolTipBodyText } from "../owner-app-dashboard/dovetail";
import { LegacyLinkAlert } from "./styles";
import { MOVED_CARDS } from "./constants";
import { useCustomerProperties } from "./useCustomerProperties";
import MovedSettingsCard from "./sections/MovedSettingsCard";
import BasicInformationCard from "./sections/BasicInformationCard";
import CustomerSuccessCard from "./sections/CustomerSuccessCard";
import ExternalVendorsCard from "./sections/ExternalVendorsCard";
import CopyPropertiesCard from "./sections/CopyPropertiesCard";
import BrandChainConfigCard from "./sections/BrandChainConfigCard";
import StationsCard from "./sections/StationsCard";
import KdsTicketColorsCard from "./sections/KdsTicketColorsCard";
import DangerZoneCard from "./sections/DangerZoneCard";

const CustomerProperties = ({ selectedCustomer, userInfo, isSelectedCustomerFlaggedForDovetail }) => {
  const properties = useCustomerProperties(selectedCustomer);
  const isBbotAdmin = Boolean(userInfo?.teams?.includes("bbot"));
  const legacyLink =
    BBOT_DJANGO_SERVER +
    (userInfo?.role === "admin" ? "/superuser/console/#!/customerProperties" : "/owner/console/#!/customerProperties");
  const doveTailToolTipBodyText = getDoveTailToolTipBodyText(
    "Customer Properties",
    isSelectedCustomerFlaggedForDovetail
  );

  return (
    <OPContainer
      title="Customer Properties"
      url="/customer-properties"
      selectedCustomer={selectedCustomer}
      userInfo={userInfo}
    >
      <div className="margin-bottom-4">
        <LegacyLinkAlert
          message={
            <span>
              We&apos;ve updated our Customer Properties page! We&apos;ll retire the old page soon but you can view it{" "}
              <a href={legacyLink}>here.</a>
            </span>
          }
        />
      </div>
      {isSelectedCustomerFlaggedForDovetail && (
        <div className="margin-bottom-4">
          <BbotAlert message={doveTailToolTipBodyText} type="warning" showIcon />
        </div>
      )}
      {properties.isLoading || !properties.customer ? (
        <PageLoadingSpinner />
      ) : (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {MOVED_CARDS.map((card) => (
            <MovedSettingsCard
              key={card.key}
              title={card.title}
              message={card.message}
              linkText={card.linkText}
              url={card.url}
            />
          ))}
          <BasicInformationCard
            timezone={properties.customer.timezone}
            tzChoices={properties.customer.tz_choices}
            currency={properties.customer.currency}
            currencies={properties.customer.currencies}
            locale={properties.customer.locale}
            locales={properties.customer.locales}
            salesforceId={properties.customer.salesforce_id}
            isDashPay={properties.customer.is_dash_pay}
            ddStoreId={properties.customer.dd_store_id}
            sevenroomsVenueId={properties.customer.integrations.sevenrooms?.sevenrooms_venue_id}
            seatedVenueId={properties.customer.integrations.seated?.seated_venue_id}
            referrer={properties.customer.referrer}
            seatedCut={properties.seatedCut}
            minimumPaidOrderCents={properties.customer.minimum_paid_order_cents}
            allowFreeOrders={properties.customer.allow_free_orders}
            allowFreeOrdersAfterPurchase={properties.customer.allow_free_orders_after_purchase}
            onTimezoneChange={(value) => properties.setCustomerField("timezone", value)}
            onCurrencyChange={(value) => properties.setCustomerField("currency", value)}
            onLocaleChange={(value) => properties.setCustomerField("locale", value)}
            onSalesforceIdChange={(value) => properties.setCustomerField("salesforce_id", value)}
            onIsDashPayChange={(value) => properties.setCustomerField("is_dash_pay", value)}
            onDdStoreIdChange={(value) => properties.setCustomerField("dd_store_id", value)}
            onSevenroomsVenueIdChange={properties.setSevenroomsVenueId}
            onSeatedVenueIdChange={properties.setSeatedVenueId}
            onSeatedCutChange={properties.setSeatedCutField}
            onSaveSeatedCut={properties.saveSeatedCut}
            onMinimumPaidOrderCentsChange={(value) => properties.setCustomerField("minimum_paid_order_cents", value)}
            onAllowFreeOrdersChange={(value) => properties.setCustomerField("allow_free_orders", value)}
            onAllowFreeOrdersAfterPurchaseChange={(value) =>
              properties.setCustomerField("allow_free_orders_after_purchase", value)
            }
            onSave={properties.save}
          />
          <CustomerSuccessCard
            customerName={properties.customer.name_for_admin}
            currentlyMuted={properties.currentlyMuted}
            inactiveCategory={properties.customer.internal_metadata.inactive_category}
            closedReason={properties.customer.internal_metadata.closed_reason}
            closedUntil={properties.customer.closed_until}
            fullstory={Boolean(properties.customer.internal_metadata.fullstory)}
            onMute={properties.muteCSAlerts}
            onInactiveCategoryChange={(value) =>
              properties.setCustomerField("internal_metadata", {
                ...properties.customer.internal_metadata,
                inactive_category: value,
              })
            }
            onClosedReasonChange={(value) =>
              properties.setCustomerField("internal_metadata", {
                ...properties.customer.internal_metadata,
                closed_reason: value,
              })
            }
            onClosedUntilChange={(value) => properties.setCustomerField("closed_until", value)}
            onUnmuteAndSave={properties.unmuteCSAlertsAndSave}
            onFullstoryChange={properties.setFullstory}
            onSave={properties.save}
          />
          <ExternalVendorsCard
            customerId={selectedCustomer?.customer_id}
            vendors={properties.externalVendors}
            allowedCustomers={properties.allowedCustomers}
            changed={properties.vendorsChanged}
            saving={properties.savingVendors}
            onAddVendor={properties.addVendor}
            onRemoveVendor={properties.removeVendor}
            onUpdateVendorField={properties.updateVendorField}
            onSave={properties.saveExternalVendors}
          />
          <CopyPropertiesCard
            customerName={properties.customer.name_for_admin}
            allowedCustomers={properties.allowedCustomers}
            config={properties.restaurantChainConfig}
            importing={properties.importingChainConfig}
            onChange={properties.setRestaurantChainConfigField}
            onImport={properties.importFromAnotherCustomer}
          />
          <BrandChainConfigCard
            customerId={selectedCustomer?.customer_id}
            brandChildren={properties.brandChildren}
            allowedCustomers={properties.allowedCustomers}
            changed={properties.brandChildrenChanged}
            saving={properties.savingBrandChildren}
            onAddBrandChild={properties.addBrandChild}
            onRemoveBrandChild={properties.removeBrandChild}
            onSave={properties.saveBrandChildren}
          />
          <StationsCard
            stations={properties.customer.stations}
            saving={properties.savingStation}
            onAddStation={properties.addStation}
            onDeleteStation={properties.deleteStation}
          />
          <KdsTicketColorsCard
            colorsByFulfillmentMethod={properties.customer.default_kds_ticket_colors}
            possibleFulfillmentMethods={properties.possibleFulfillmentMethods}
            saving={properties.saving}
            onAddColor={properties.addKdsColor}
            onRemoveColor={properties.removeKdsColor}
            onColorChange={properties.setKdsColor}
            onSave={properties.save}
          />
          {(properties.customer.can_delete ||
            !properties.customer.is_archived ||
            isBbotAdmin ||
            properties.customer.can_be_reactivated) && (
            <DangerZoneCard
              isBbotAdmin={isBbotAdmin}
              selectedReferrer={properties.selectedReferrer}
              possibleReferrers={properties.customer.possible_referrers}
              switching={properties.switchingReferrer}
              onChangeReferrer={properties.setSelectedReferrer}
              onSwitchReferrer={properties.switchReferrer}
              canDelete={properties.customer.can_delete}
              isArchived={properties.customer.is_archived}
              canBeReactivated={properties.customer.can_be_reactivated}
              reactivating={properties.reactivating}
              onDelete={properties.deleteCustomerAccount}
              onDeactivate={properties.deactivateCustomer}
              onReactivate={properties.reactivateCustomer}
            />
          )}
        </Space>
      )}
    </OPContainer>
  );
};

export default CustomerProperties;
